import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Tabs,
  Tab
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  LockOpen as LockOpenIcon,
  CheckCircle as CheckCircleIcon,
  Code as CodeIcon,
  CloudDownload as CloudDownloadIcon,
  SwapHoriz as SwapHorizIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  Shield as ShieldIcon,
  CloudDone as CloudDoneIcon,
  Verified as VerifiedIcon,
  Accessibility as AccessibilityIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';

const NoVendorLockIn: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = React.useState(5);
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    const urls = [
      '/capabilities/high-performance',
      '/capabilities/zero-trust-security',
      '/capabilities/fedramp-ready',
      '/capabilities/section-508-compliant',
      '/capabilities/scalable-architecture',
      '/capabilities/no-vendor-lock-in'
    ];
    navigate(urls[newValue]);
  };

  const benefits = [
    'Open standards and APIs ensure interoperability',
    'Standard data formats enable easy migration',
    'RESTful APIs for seamless integration',
    'Export capabilities for complete data portability',
    'No proprietary formats or protocols',
    'Freedom to switch providers or bring services in-house'
  ];

  const capabilities = [
    {
      icon: <CodeIcon sx={{ color: 'secondary.main' }} />,
      title: 'Open Standards',
      description: 'Built on industry-standard protocols and data formats (JSON, REST, OAuth)'
    },
    {
      icon: <SwapHorizIcon sx={{ color: 'secondary.main' }} />,
      title: 'API-First Design',
      description: 'Comprehensive RESTful APIs enable integration with any system'
    },
    {
      icon: <CloudDownloadIcon sx={{ color: 'secondary.main' }} />,
      title: 'Data Portability',
      description: 'Export all data in standard formats for migration or backup'
    },
    {
      icon: <SwapHorizIcon sx={{ color: 'secondary.main' }} />,
      title: 'Flexible Integration',
      description: 'Connect with existing tools and systems without vendor restrictions'
    }
  ];

  const useCases = [
    {
      title: 'Multi-Vendor Strategy',
      description: 'Integrate E-Unify with best-of-breed tools without being locked into a single vendor ecosystem'
    },
    {
      title: 'Future-Proofing',
      description: 'Protect your investment with the flexibility to adapt as technology and requirements evolve'
    },
    {
      title: 'Data Sovereignty',
      description: 'Maintain complete control over your data with full export and migration capabilities'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3, color: 'primary.main' }}
      >
        Back to About Page
      </Button>

      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        aria-label="Core Capability tabs"
        variant="scrollable"
        scrollButtons="auto"
        sx={{ 
          mb: 4,
          '.MuiTab-root': {
            color: 'primary.main',
            minHeight: 64,
            '&.Mui-selected': {
              color: 'primary.main',
              fontWeight: 700
            }
          },
          '.MuiTabs-indicator': {
            backgroundColor: 'secondary.main',
            height: 3
          }
        }}
      >
        <Tab label="High Performance" icon={<SpeedIcon />} iconPosition="start" />
        <Tab label="Zero Trust" icon={<ShieldIcon />} iconPosition="start" />
        <Tab label="FedRAMP Ready" icon={<CloudDoneIcon />} iconPosition="start" />
        <Tab label="508 Compliant" icon={<AccessibilityIcon />} iconPosition="start" />
        <Tab label="Scalable" icon={<TrendingUpIcon />} iconPosition="start" />
        <Tab label="No Lock-In" icon={<LockOpenIcon />} iconPosition="start" />
      </Tabs>

      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <LockOpenIcon sx={{ fontSize: 48, color: 'secondary.main', mr: 2 }} />
          <Typography variant="h3" sx={{ color: 'secondary.main', fontWeight: 'bold' }}>
            No Vendor Lock-In
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary">
          Open standards and APIs ensure flexibility and future-proofing
        </Typography>
      </Box>

      <Paper sx={{ p: 4, mb: 4, borderLeft: '4px solid #B31B1B' }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom sx={{ color: 'secondary.main', fontWeight: 600 }}>
              Overview
            </Typography>
            <Typography paragraph sx={{ fontSize: '0.95rem' }}>
              E-Unify is built on open standards and provides comprehensive APIs to ensure you're never locked into a proprietary ecosystem. Our commitment to interoperability means you can integrate with any tool, export your data at any time, and maintain complete control over your metadata management strategy.
            </Typography>
            <Typography sx={{ fontSize: '0.95rem' }}>
              By avoiding proprietary formats and protocols, E-Unify protects your investment and gives you the freedom to adapt as your needs change. Whether you want to integrate with existing tools, migrate to a different solution, or bring services in-house, E-Unify provides the flexibility you need.
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom sx={{ color: 'secondary.main', fontWeight: 600 }}>
              Key Benefits
            </Typography>
            <List dense>
              {benefits.map((benefit, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckCircleIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={benefit}
                    primaryTypographyProps={{ fontSize: '0.95rem' }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="h5" gutterBottom sx={{ color: 'secondary.main', fontWeight: 600, mb: 3 }}>
        Openness Capabilities
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {capabilities.map((capability, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card sx={{ height: '100%', borderLeft: '4px solid #B31B1B' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {capability.icon}
                  <Typography variant="h6" sx={{ ml: 1, fontWeight: 600 }}>
                    {capability.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {capability.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography variant="h5" gutterBottom sx={{ color: 'secondary.main', fontWeight: 600, mb: 3 }}>
        Common Use Cases
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {useCases.map((useCase, index) => (
          <Grid item xs={12} key={index}>
            <Paper sx={{ p: 3, borderLeft: '4px solid #B31B1B' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                {useCase.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {useCase.description}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper 
        sx={{ 
          p: 4, 
          textAlign: 'center', 
          backgroundColor: 'secondary.main',
          color: 'white'
        }}
      >
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Freedom and Flexibility
        </Typography>
        <Typography variant="body1" paragraph>
          Learn how E-Unify protects your investment with open standards
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/contact')}
          sx={{
            bgcolor: 'white',
            color: 'secondary.main',
            '&:hover': {
              bgcolor: '#f0f0f0'
            }
          }}
        >
          Contact Us
        </Button>
      </Paper>
    </Container>
  );
};

export default NoVendorLockIn;
